import { getUnixTime } from "date-fns"
import { OperationOutcome } from "fhir/r4"
import { Button } from "nhsuk-react-components"
import { ReactNode, createContext, useCallback, useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useCheckTokenMutation } from "../api/checkToken"
import { logout } from "../api/logout"
import { AuthResponse, refreshToken } from "../api/refreshToken"
import { Loading } from "../components/loading/Loading"
import { Modal } from "../components/Modal/Modal"
import { useIdleTimer } from "../hooks/useIdleTimer"
import { ErrorPage } from "../pages/error/ErrorPage"
import { LoggedOut } from "../pages/loggedOut/LoggedOut"
import "./AuthenticationContext.scss"

type AuthenticationContextProps = {
  changeRequested: boolean
  token?: string
  leaveSite: () => void
  setChangeRequested: (value: boolean) => void
}
type AuthStatus = AuthResponse | OperationOutcome | undefined | { status: "logged-out" | "timed-out" }

const INITIAL_CONTEXT: AuthenticationContextProps = {
  token: undefined,
  leaveSite: function (): void {
    throw new Error("Function not implemented.")
  },
  setChangeRequested: function (_value: boolean): void {
    throw new Error("Function not implemented.")
  },
  changeRequested: false,
}

const authIsError = (authStatus: AuthStatus): authStatus is OperationOutcome =>
  authStatus !== undefined && "resourceType" in authStatus && authStatus.resourceType === "OperationOutcome"
const authIsSecurityError = (authStatus: AuthStatus): authStatus is OperationOutcome =>
  authIsError(authStatus) && (authStatus?.issue ?? []).find((issue) => issue.code === "security") !== undefined
const authIsSuccess = (authStatus: AuthStatus): authStatus is AuthResponse =>
  authStatus !== undefined && "access_token" in authStatus && "expires_at" in authStatus
const userIsLoggedOut = (authStatus: AuthStatus) =>
  authStatus !== undefined && "status" in authStatus && authStatus.status === "logged-out"
const userIsTimedOut = (authStatus: AuthStatus) =>
  authStatus !== undefined && "status" in authStatus && authStatus.status === "timed-out"

//eslint-disable-next-line react-refresh/only-export-components
export const AuthenticationContext = createContext<AuthenticationContextProps>(INITIAL_CONTEXT)

// Token auto validation constants
const TEN_MINS = 10 * 60 * 1000
const FIVE_MINS = 5 * 60 * 1000

export function AuthenticationProvider({ children }: { children: ReactNode }) {
  const [wayfinderAuth, setWayfinderAuth] = useState<AuthStatus | undefined>(undefined)
  const [changeRequested, setChangeRequested] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [searchParams] = useSearchParams()

  const [idleTime, setIdleTime] = useState<number | null>(null)
  const { isIdle, refreshTimer } = useIdleTimer(idleTime ?? FIVE_MINS * 0.25)
  const [idleCheckEnabled, setIdleCheckEnabled] = useState(true)

  const { mutateAsync: validateToken, isLoading } = useCheckTokenMutation()

  const token = wayfinderAuth !== undefined && "access_token" in wayfinderAuth ? wayfinderAuth.access_token : ""

  const onCheckToken = useCallback(async () => {
    if (isLoading) return

    const res = await validateToken(token)
    if (!res) {
      setWayfinderAuth({ status: "logged-out" })
      setIsOpen(false)
      setIdleCheckEnabled(false)
    } else {
      refreshTimer()
    }
  }, [isLoading, refreshTimer, validateToken, token])

  useEffect(() => {
    if (isIdle && idleCheckEnabled) {
      onCheckToken()
    }
  }, [isIdle, idleCheckEnabled, onCheckToken])

  useEffect(() => {
    if (idleCheckEnabled && wayfinderAuth && "expires_at" in wayfinderAuth) {
      const tokenTime = wayfinderAuth.expires_at * 1000 - Date.now()
      const newIdleTime = tokenTime <= TEN_MINS ? tokenTime / 2 : FIVE_MINS
      setIdleTime(newIdleTime)
    }
  }, [idleCheckEnabled, wayfinderAuth])

  const tryToRefreshToken = useCallback(async () => {
    const res = await refreshToken(
      wayfinderAuth !== undefined && "access_token" in wayfinderAuth ? wayfinderAuth.access_token : "",
    )
    setWayfinderAuth(res)
    setIsOpen(false)
  }, [wayfinderAuth])

  const accessToken = searchParams.get("accessToken") ?? ""

  useEffect(() => {
    if (accessToken && wayfinderAuth === undefined) {
      // eslint-disable-next-line @typescript-eslint/no-extra-semi
      ;(async () => {
        const res = await refreshToken(accessToken)
        if (res) {
          setWayfinderAuth(res)
        } else {
          setWayfinderAuth({ status: "logged-out" })
        }
      })()
    }
  }, [accessToken, wayfinderAuth])

  useEffect(() => {
    const checkTokenExpiry = () => {
      const timeoutTime = authIsSuccess(wayfinderAuth) ? wayfinderAuth.expires_at : undefined

      if (timeoutTime) {
        const timeToTimeout = timeoutTime - getUnixTime(new Date())
        if (timeToTimeout <= 1) {
          setWayfinderAuth({ status: "timed-out" })
          setIsOpen(false)
        } else if (timeToTimeout <= 60) {
          setIsOpen(true)
        }
      }
    }
    const interval = setInterval(() => {
      checkTokenExpiry()
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [wayfinderAuth])

  const leaveSite = async () => {
    await logout(wayfinderAuth !== undefined && "access_token" in wayfinderAuth ? wayfinderAuth.access_token : "")
    setWayfinderAuth({ status: "logged-out" })
    setIsOpen(false)
  }

  const pageContent = () => {
    if (wayfinderAuth === undefined && !accessToken) return <ErrorPage />
    if (wayfinderAuth === undefined) return <Loading />
    if (authIsSecurityError(wayfinderAuth)) return <LoggedOut userIsLoggedOut={false} userIsTimedOut={false} />
    if (authIsError(wayfinderAuth)) return <ErrorPage />
    if (userIsTimedOut(wayfinderAuth)) return <LoggedOut userIsLoggedOut={false} userIsTimedOut={true} />
    if (userIsLoggedOut(wayfinderAuth)) return <LoggedOut userIsLoggedOut={true} userIsTimedOut={false} />
    if (authIsSuccess(wayfinderAuth)) return children

    return <Loading />
  }

  return (
    <AuthenticationContext.Provider
      value={{
        changeRequested,
        token: wayfinderAuth !== undefined && "access_token" in wayfinderAuth ? wayfinderAuth.access_token : "",
        leaveSite,
        setChangeRequested,
      }}
    >
      {pageContent()}
      {isOpen && (
        <Modal.Root isOpen={isOpen} onOpenChange={setIsOpen}>
          <Modal.Body data-testid="modal-content">
            <p className="security-timeout-message" aria-label="heading">
              For security reasons, you’ll be logged out in 1 minute.
            </p>
            <Button onClick={tryToRefreshToken}>Stay logged in</Button>
            <div onClick={leaveSite} className="nhsuk-header__navigation-item logout-link" data-testid="modal-logout">
              Log out
            </div>
          </Modal.Body>
        </Modal.Root>
      )}
    </AuthenticationContext.Provider>
  )
}
